import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { GameFrame, GameShell, Spinner } from './EnvLayout';
import { htmlGameLoader } from './gameRegistry';
import SurveyPage from './SurveyPage';
import CalculatorGame from './CalculatorGame';
import PlantGame from './PlantGame';

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">{children}</p>
      <Link to="/environmental" className="mt-6 inline-block text-sm font-semibold text-slate-500 dark:text-slate-400 underline">
        Back to games
      </Link>
    </div>
  );
}

/** A standalone HTML game dropped into src/assets/games/<slug>.html. */
function HtmlGame({ slug, title }: { slug: string; title: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const loader = htmlGameLoader(slug);

  useEffect(() => {
    let alive = true;
    loader?.().then(h => { if (alive) setHtml(h); });
    return () => { alive = false; };
  }, [loader]);

  if (!loader) return <Message>{title} is coming soon.</Message>;
  if (html === null) return <Spinner />;
  return <GameFrame html={html} title={title} />;
}

export default function EnvironmentalGamePage() {
  const { slug = '' } = useParams();
  const { data: games, isLoading } = trpc.environmental.games.useQuery();

  if (isLoading) return <Spinner />;
  const game = games?.find(g => g.slug === slug);
  if (!game) return <GameShell title="Not available"><Message>This game isn't available.</Message></GameShell>;

  if (slug === 'survey') return <SurveyPage title={game.title} />;
  if (slug === 'carbon-calculator') return <CalculatorGame title={game.title} />;
  if (slug === 'plant') return <PlantGame title={game.title} />;

  return (
    <GameShell title={game.title}>
      <HtmlGame slug={slug} title={game.title} />
    </GameShell>
  );
}
